import { AxiosStatic } from 'axios'
import { LeaguestarState } from './state'
import { extractApiError, NetworkError, ApiError } from './common/errors'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import {
  getActiveDeviceToken,
  getActiveDevicePlatformId,
} from './device-link/api'

export function getCookie(name: string) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

export const configureAxiosClient = async (axios: AxiosStatic) => {
  // For native apps, get API URL from iOS/Android configuration stored in Preferences
  if (Capacitor.isNativePlatform()) {
    try {
      const { value } = await Preferences.get({ key: 'apiBaseUrl' })
      if (value) {
        axios.defaults.baseURL = value
        console.log('API Base URL configured from native:', value)
      } else {
        console.warn('API URL from native Preferences is empty')
        // Fallback for native if Preferences is empty
        if (process.env.REACT_APP_API_URL) {
          axios.defaults.baseURL = process.env.REACT_APP_API_URL
          console.log('Using fallback API URL:', process.env.REACT_APP_API_URL)
        }
      }
    } catch (error) {
      console.error('Failed to get API URL from Preferences:', error)
      // Fallback for native if Preferences fails
      if (process.env.REACT_APP_API_URL) {
        axios.defaults.baseURL = process.env.REACT_APP_API_URL
        console.log('Using fallback API URL:', process.env.REACT_APP_API_URL)
      }
    }
  } else {
    // For web, use environment variable if provided, otherwise use relative URLs
    if (process.env.REACT_APP_API_URL) {
      axios.defaults.baseURL = process.env.REACT_APP_API_URL
      console.log(
        'API Base URL configured for web:',
        process.env.REACT_APP_API_URL
      )
    }
  }

  console.log(
    'Final axios.defaults.baseURL:',
    axios.defaults.baseURL || '(relative URLs)'
  )

  axios.interceptors.request.use((config) => {
    const deviceToken = getActiveDeviceToken()
    const devicePlatformId = getActiveDevicePlatformId()
    const currentState = LeaguestarState.getRawState()

    // Use device auth if available, otherwise session auth
    const platformId =
      devicePlatformId ??
      currentState.selected_platform_id ??
      currentState.platforms[0]?.platform_id ??
      1

    config.params = {
      ...config.params,
      platform_id: platformId,
    }

    if (deviceToken) {
      config.headers!['Authorization'] = `Bearer ${deviceToken}`
    } else {
      config.headers!['X-CSRFToken'] = getCookie('csrftoken')
    }

    return config
  })

  axios.interceptors.response.use(
    (response) => {
      const buildtime = response.headers['x-leaguestar-buildtime']

      const currentState = LeaguestarState.getRawState()

      if (buildtime && buildtime !== currentState.api_build_timestamp) {
        LeaguestarState.update((state) => {
          state.api_build_timestamp = buildtime
        })
      }

      return response
    },
    (error) => {
      const endpoint = error.config?.url || 'unknown'
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
      const requestData = error.config?.data

      const extracted = extractApiError(error, endpoint, method, requestData)

      if (
        extracted instanceof NetworkError ||
        (extracted instanceof ApiError && extracted.statusCode >= 500)
      ) {
        if (!extracted.context.sentryReported) {
          extracted.reportToSentry()
          extracted.context.sentryReported = true
        }
      }

      return Promise.reject(extracted)
    }
  )
}
